// src/pages/offlineSales/PaymentMethodsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Pencil, X } from 'lucide-react';
import { useAlert } from '../../contexts/AlertContext';
import {
  getAllPaymentMethods,
  updatePaymentMethodSettings,
  assignPaymentMethod,
  unassignPaymentMethod,
  updatePaymentMethod,
} from '../../services/api/offlineSales/paymentMethods';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { PaymentMethodIcon, getPaymentMethodIconUrl } from '../../utils/paymentMethodIcons';
import { DataTable } from '../../components/common';
import styles from './PaymentMethodsPage.module.css';

// ─── Fee type options ──────────────────────────────────────────────────────
const FEE_TYPE_OPTIONS = [
  { value: 'none', label: 'No fee' },
  { value: 'flat', label: 'Flat amount' },
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'flat_and_percentage', label: 'Flat + Percentage' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────
const describeFee = (method) => {
  if (method.feeType === 'none') return null;
  const flat = parseFloat(method.feeFlat) || 0;
  const pct = parseFloat(method.feePercentage) || 0;
  if (method.feeType === 'flat') return `Rs. ${flat.toFixed(2)}`;
  if (method.feeType === 'percentage') return `${pct}%`;
  if (method.feeType === 'flat_and_percentage') return `Rs. ${flat.toFixed(2)} + ${pct}%`;
  return null;
};

// ─── Payment Method Edit Modal (edit-only — assignment happens from the table) ──
const PaymentMethodModal = ({ method, onSave, onClose, saving }) => {
  const [form, setForm] = useState(() => ({
    feeType: method.feeType || 'none',
    feeFlat: method.feeFlat != null ? String(method.feeFlat) : '',
    feePercentage: method.feePercentage != null ? String(method.feePercentage) : '',
    feeChargedToCustomer: Boolean(method.feeChargedToCustomer),
    sortOrder: method.sortOrder || 0,
  }));

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      feeType: form.feeType,
      feeFlat: form.feeType === 'flat' || form.feeType === 'flat_and_percentage'
        ? parseFloat(form.feeFlat) || 0 : 0,
      feePercentage: form.feeType === 'percentage' || form.feeType === 'flat_and_percentage'
        ? parseFloat(form.feePercentage) || 0 : 0,
      feeChargedToCustomer: form.feeChargedToCustomer,
      sortOrder: parseInt(form.sortOrder) || 0,
    };
    onSave(payload);
  };

  const showFlat = form.feeType === 'flat' || form.feeType === 'flat_and_percentage';
  const showPct = form.feeType === 'percentage' || form.feeType === 'flat_and_percentage';
  const showFeeTarget = form.feeType !== 'none';

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Edit Payment Method</h3>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody}>
            {/* Fee configuration */}
            <div className={styles.feeRow}>
              <div className={styles.feeRowTitle}>Fee Configuration</div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Fee Type</label>
                <Dropdown
                  value={form.feeType}
                  onChange={value => set('feeType', value || 'none')}
                  options={FEE_TYPE_OPTIONS}
                  allowClear={false}
                />
              </div>

              {showFlat && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Flat Fee Amount (Rs.)</label>
                  <input
                    className={styles.input}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.feeFlat}
                    onChange={e => set('feeFlat', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              )}

              {showPct && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Percentage Fee (%)</label>
                  <input
                    className={styles.input}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.feePercentage}
                    onChange={e => set('feePercentage', e.target.value)}
                    placeholder="0.00"
                  />
                  <span className={styles.fieldHint}>e.g. enter 3 for 3%</span>
                </div>
              )}

              {showFeeTarget && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Fee charged to</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={form.feeChargedToCustomer === true}
                        onChange={() => set('feeChargedToCustomer', true)}
                      />
                      Customer (added to total)
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        checked={form.feeChargedToCustomer === false}
                        onChange={() => set('feeChargedToCustomer', false)}
                      />
                      Business (absorbed)
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Sort order */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Sort Order</label>
              <input
                className={styles.input}
                type="number"
                min="0"
                step="1"
                value={form.sortOrder}
                onChange={e => set('sortOrder', e.target.value)}
                style={{ maxWidth: '100px' }}
              />
              <span className={styles.fieldHint}>Lower numbers appear first.</span>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnSecondary} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────
const PaymentMethodsPage = () => {
  const { success, error } = useAlert();

  const [methods, setMethods] = useState([]);
  const [customizeEnabled, setCustomizeEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal state: null = closed, method obj = edit
  const [modalData, setModalData] = useState(null);

  const fetchMethods = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllPaymentMethods();
      setCustomizeEnabled(Boolean(data?.customizePaymentMethods));
      const list = Array.isArray(data?.methods) ? data.methods : [];
      // DataTable keys/selects rows by `.id` — alias it to paymentMethodId (the global catalog id).
      setMethods(list.map(m => ({ ...m, id: m.paymentMethodId })));
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleToggleCustomize = async () => {
    const next = !customizeEnabled;
    setCustomizeEnabled(next);
    try {
      await updatePaymentMethodSettings({ customizePaymentMethods: next });
      success(`Customize Payment Methods ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setCustomizeEnabled(!next);
      error(err?.response?.data?.message || 'Failed to update setting');
    }
  };

  const handleToggleAssign = async (method) => {
    if (!customizeEnabled) return;
    const wasAssigned = method.isAssigned;
    setMethods(prev => prev.map(m => m.paymentMethodId === method.paymentMethodId ? { ...m, isAssigned: !wasAssigned } : m));
    try {
      if (wasAssigned) {
        await unassignPaymentMethod(method.paymentMethodId);
        success('Payment method unassigned');
      } else {
        const result = await assignPaymentMethod(method.paymentMethodId);
        setMethods(prev => prev.map(m => m.paymentMethodId === method.paymentMethodId
          ? {
            ...m,
            isAssigned: true,
            feeType: result.assignment?.feeType ?? m.feeType,
            feeFlat: result.assignment?.feeFlat ?? m.feeFlat,
            feePercentage: result.assignment?.feePercentage ?? m.feePercentage,
            feeChargedToCustomer: result.assignment?.feeChargedToCustomer ?? m.feeChargedToCustomer,
            sortOrder: result.assignment?.sortOrder ?? m.sortOrder,
          }
          : m));
        success('Payment method assigned');
      }
    } catch (err) {
      setMethods(prev => prev.map(m => m.paymentMethodId === method.paymentMethodId ? { ...m, isAssigned: wasAssigned } : m));
      error(err?.response?.data?.message || 'Failed to update payment method assignment');
    }
  };

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      const result = await updatePaymentMethod(modalData.paymentMethodId, payload);
      setMethods(prev => prev.map(m => m.paymentMethodId === modalData.paymentMethodId ? { ...m, ...result.method } : m));
      success('Payment method updated');
      setModalData(null);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderLeft}>
            <CreditCard size={18} className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>Payment Methods</h2>
          </div>
          <div className={styles.toggleRow}>
            <div className={styles.toggleInfo}>
              <span className={styles.toggleLabel}>Customize Payment Methods</span>
              <span className={styles.toggleHint}>
                Choose which payment methods your business accepts and set their fees.
              </span>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={customizeEnabled}
                onChange={handleToggleCustomize}
              />
              <span className={styles.slider} />
            </label>
          </div>
        </div>

        {methods.length === 0 ? (
          <div className={styles.emptyState}>
            No payment methods found.
          </div>
        ) : (
          <DataTable
            data={methods}
            columns={[
              {
                key: 'name',
                header: 'Name / Key',
                render: (method) => (
                  <>
                    <div className={styles.methodNameCell}>
                      {getPaymentMethodIconUrl(method.iconFileName) && (
                        <PaymentMethodIcon iconFileName={method.iconFileName} label={method.label} width={24} />
                      )}
                      <span className={styles.methodName}>{method.label}</span>
                    </div>
                    <div>
                      <span className={styles.methodKey}>{method.key}</span>
                    </div>
                  </>
                ),
              },
              {
                key: 'fee',
                header: 'Fee',
                render: (method) => {
                  const fee = describeFee(method);
                  return fee
                    ? <span className={styles.feeDescription}>{fee}</span>
                    : <span className={styles.feeNone}>None</span>;
                },
              },
              {
                key: 'feeChargedTo',
                header: 'Fee Charged To',
                render: (method) => method.feeType !== 'none' ? (
                  <span className={`${styles.feeTarget} ${method.feeChargedToCustomer ? styles.feeTargetCustomer : styles.feeTargetBusiness}`}>
                    {method.feeChargedToCustomer ? 'Customer' : 'Business'}
                  </span>
                ) : (
                  <span className={styles.feeNone}>—</span>
                ),
              },
              { key: 'sortOrder', header: 'Sort', render: (method) => method.sortOrder },
              {
                key: 'assign',
                header: 'Assign',
                render: (method) => (
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={!customizeEnabled ? true : method.isAssigned}
                      disabled={!customizeEnabled}
                      onChange={() => handleToggleAssign(method)}
                    />
                    <span className={styles.slider} />
                  </label>
                ),
              },
            ]}
            actions={{
              render: (method) => (
                <div className={styles.actionBtns}>
                  <button
                    className={styles.editBtn}
                    title={!customizeEnabled || !method.isAssigned ? 'Assign this method before editing its fees' : 'Edit'}
                    disabled={!customizeEnabled || !method.isAssigned}
                    onClick={() => setModalData(method)}
                  >
                    <Pencil size={14} />
                  </button>
                </div>
              ),
            }}
          />
        )}
      </div>

      {/* Edit modal */}
      {modalData !== null && (
        <PaymentMethodModal
          method={modalData}
          onSave={handleSave}
          onClose={() => setModalData(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default PaymentMethodsPage;
